/*
 * @Author: Auto
 * @Date: 2025-01-27
 * @Description: 小微信贷详情页面
 */
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import styles from './index.scss'
import LoanApplyModal from '../components/loan-apply-modal'
import Loan from '@/modules/loan'
import { normalizeProductDetail, parseProductDescriptions } from '../loanAdapter'
import User from '@/modules/user'
import dtlIconFile from '@/common/images/dtl-icon-file.png'
import dtlIconDoc from '@/common/images/dtl-icon-doc.png'
import dtlIconStart from '@/common/images/dtl-icon-start.png'
import { doLogin } from '@/events'

const SmallLoanDetail = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [detailData, setDetailData] = useState(null)
  const [originDetailData, setOriginDetailData] = useState(null)
  const [loading, setLoading] = useState(true)
  const loanApplyModalRef = useRef(null)

  const handleApply = () => {
    const user = new User()
    if (!user.getLoginStatu()) {
      doLogin()
      return
    }
    loanApplyModalRef.current?.open(originDetailData)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    setLoading(true)
    Loan.getProductDetails({ productId: id })
      .then(res => {
        setOriginDetailData(res)
        const normalized = normalizeProductDetail(res)
        const descriptions = parseProductDescriptions(res.productDescs)
        setDetailData({
          ...normalized,
          process: descriptions.loanprocess.map((title, index) => ({
            step: String(index + 1).padStart(2, '0'),
            title
          })),
          features: descriptions.specialities,
          materials: descriptions.materials,
          conditions: descriptions.terms
        })
      })
      .catch(err => {
        console.error('Get product details error:', err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [id])

  const handleBack = () => {
    navigate('/small-loan')
  }

  if (loading) {
    return (
      <div className={styles['small-loan-detail']}>
        <div className={styles['loading']}>加载中...</div>
      </div>
    )
  }

  return (
    <div className={styles['small-loan-detail']}>
      {/* 顶部背景区域 */}
      <div className={styles['top-banner']}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          className={styles['back-btn']}
          type='text'
        >
          返回
        </Button>
      </div>

      <div className={styles['main-container']}>
        {/* 头部卡片 */}
        <div className={styles['card-shadow']}>
          <div className={styles['header-card']}>
            <div className={styles['bank-info']}>
              <div className={styles['bank-logo-placeholder']} >
                {detailData?.bankName?.substring(0, 1) || '银'}
              </div>
              <span className={styles['bank-name']}>{detailData?.bankName}</span>
            </div>
            <h1 className={styles['loan-title']}>{detailData?.title}</h1>
            <p className={styles['loan-desc']} title={detailData?.description}>{detailData?.description}</p>

            <div className={styles['loan-metrics']}>
              <div className={styles['metric-item']}>
                <div className={styles['label']}>最高额度</div>
                <div className={styles['value']}>¥ {detailData?.amountText}</div>
              </div>
              <div className={styles['divider']} />
              <div className={styles['metric-item']}>
                <div className={styles['label']}>最长期限</div>
                <div className={styles['value']}>{detailData?.termText}</div>
              </div>
              <div className={styles['divider']} />
              <div className={styles['metric-item']}>
                <div className={styles['label']}>{detailData?.ratesUnitDesc}利率</div>
                <div className={styles['value']}>{detailData?.rateText}</div>
              </div>
              <div className={styles['apply-btn-wrapper']}>
                <Button type='primary' className={styles['apply-btn']} onClick={handleApply}>立即申请</Button>
              </div>
            </div>
          </div>

        </div>

        {/* 基础信息 */}
        <div className={styles['content-card']}>
          <h2 className={styles['section-title']}>基础信息</h2>
          <div className={styles['info-grid']}>
            <div className={styles['info-item']}>
              <div className={styles['label']}>贷款对象</div>
              <div className={styles['value']}>{detailData?.basicInfo?.target}</div>
            </div>
            <div className={styles['info-divider']} />
            <div className={styles['info-item']}>
              <div className={styles['label']}>贷款用途</div>
              <div className={styles['value']}>{detailData?.basicInfo?.purpose}</div>
            </div>
            <div className={styles['info-divider']} />
            <div className={styles['info-item']}>
              <div className={styles['label']}>受理模式</div>
              <div className={styles['value']}>{detailData?.basicInfo?.mode}</div>
            </div>
            <div className={styles['info-divider']} />
            <div className={styles['info-item']}>
              <div className={styles['label']}>还款方式</div>
              <div className={styles['value']}>{detailData?.basicInfo?.repayment}</div>
            </div>
          </div>
        </div>

        {/* 贷款流程 */}
        <div className={styles['content-card']}>
          <h2 className={styles['section-title']}>贷款流程</h2>
          <div className={styles['process-steps']}>
            {detailData?.process?.length ? detailData.process.map((step, index) => (
              <React.Fragment key={index}>
                <div className={styles['step-item']}>
                  <div className={styles['step-icon']}>{step.step}</div>
                  <div className={styles['step-text']}>
                    <span className={styles['check-icon']}>✓</span>
                    {step.title}
                  </div>
                </div>
                {index < detailData.process.length - 1 && (
                  <div className={styles['step-line']} />
                )}
              </React.Fragment>
            )) : <span>暂无相关信息</span>}
          </div>
        </div>

        {/* 产品特色 */}
        <div className={styles['content-card']}>
          <h2 className={styles['section-title']}>产品特色</h2>
          <div className={styles['features-list']}>
            <img className={styles['feature-icon-placeholder']} src={dtlIconStart} alt='' />
            <ul className={`${styles['common-list']} ${styles['feature-list']}`}>
              {detailData?.features?.length ? detailData.features.map((item, index) => (
                <li key={index}>{item}</li>
              )) : <li>暂无相关信息</li>}
            </ul>

            <div className={styles['feature-bg-placeholder']} />
          </div>
        </div>

        {/* 申请资料 */}
        <div className={styles['content-card']}>
          <h2 className={styles['section-title']}>申请资料</h2>
          <div className={styles['materials-section']}>
            <img className={styles['materials-icon-placeholder']} src={dtlIconFile} alt='' />
            <ul className={styles['common-list']}>
              {detailData?.materials?.length ? detailData.materials.map((item, index) => (
                <li key={index}>{item}</li>
              )) : <li>暂无相关信息</li>}
            </ul>
            <div className={styles['materials-bg-placeholder']} />
          </div>
        </div>

        {/* 办理条件 */}
        <div className={styles['content-card']}>
          <h2 className={styles['section-title']}>办理条件</h2>
          <div className={styles['conditions-section']}>
            <img className={styles['conditions-icon-placeholder']} src={dtlIconDoc} alt='' />
            <ul className={styles['common-list']}>
              {detailData?.conditions?.length ? detailData.conditions.map((item, index) => (
                <li key={index}>{item}</li>
              )) : <li>暂无相关信息</li>}
            </ul>
            <div className={styles['conditions-bg-placeholder']} />
          </div>
        </div>

        <div className={styles['bottom-action']}>
          <Button type='primary' size='large' className={styles['bottom-apply-btn']} onClick={handleApply}>立即申请</Button>
        </div>
      </div>
      <LoanApplyModal ref={loanApplyModalRef} />
    </div>
  )
}

export default SmallLoanDetail

